import { useState, useMemo, Fragment, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getYearFromDateString, parseDateString, formatDateString, daysBetween } from '../lib/dateUtils';

interface Child {
  id: string;
  name: string;
  camp_schedules?: Array<{
    id: string;
    camp_id: string;
    child_id?: string;
    start_date: string;
    end_date: string;
    event_year?: number | null;
    camp?: {
      id: string;
      name: string;
      description?: string;
      address?: string;
      city?: string;
      state?: string;
      zip_code?: string;
    };
  }>;
}

interface CalendarPreviewViewProps {
  children: Child[];
  selectedYear: number;
  onYearChange?: (year: number) => void;
}

const CHILD_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function CalendarPreviewView({
  children,
  selectedYear: initialYear,
  onYearChange
}: CalendarPreviewViewProps) {
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    const currentYear = today.getFullYear();
    
    // Only apply smart month logic if we're viewing the current year
    if (initialYear === currentYear) {
      // If less than 10 days into current month, show current month
      if (currentDay < 10) {
        return currentMonth;
      }
      
      // If 10+ days into current month, show next month
      // Handle year rollover (e.g., December -> January of next year)
      const nextMonth = currentMonth + 1;
      if (nextMonth > 11) {
        // We're in December, will show January of next year
        // But keep the year as initialYear for now, useEffect will handle it
        return 0; // January
      }
      return nextMonth;
    }
    
    // If viewing a different year (past or future), show January
    return 0;
  });

  // Update selectedYear when initialYear changes
  // Also update month if we need to show next month/year
  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    const currentYear = today.getFullYear();
    
    // Only apply smart month logic if we're viewing the current year
    if (initialYear === currentYear) {
      if (currentDay >= 10) {
        // 10+ days into current month, show next month
        const nextMonth = currentMonth + 1;
        if (nextMonth > 11) {
          // We're in December, show January of next year
          setSelectedYear(currentYear + 1);
          setSelectedMonth(0); // January
        } else {
          // Next month is still in current year
          setSelectedYear(initialYear);
          setSelectedMonth(nextMonth);
        }
      } else {
        // Less than 10 days, show current month
        setSelectedYear(initialYear);
        setSelectedMonth(currentMonth);
      }
    } else {
      // Viewing a different year, use the initialYear and show January
      setSelectedYear(initialYear);
      setSelectedMonth(0); // January
    }
  }, [initialYear]);

  // Get available years from schedules, always include current selectedYear
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    // Always include the current selected year
    years.add(selectedYear);
    // Add years from schedules (check both start_date and end_date)
    children.forEach(child => {
      (child.camp_schedules || []).forEach(schedule => {
        const startYear = getYearFromDateString(schedule.start_date);
        const endYear = getYearFromDateString(schedule.end_date);
        if (startYear !== null) {
          years.add(startYear);
        }
        if (endYear !== null && endYear !== startYear) {
          years.add(endYear);
        }
      });
    });
    // Also add current year and next year if not already present
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    years.add(currentYear + 1);
    return Array.from(years).sort();
  }, [children, selectedYear]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (onYearChange) {
      onYearChange(year);
    }
  };

  // Create color map for children
  const childColorMap = useMemo(() => {
    const map = new Map<string, string>();
    children.forEach((child, index) => {
      map.set(child.id, CHILD_COLORS[index % CHILD_COLORS.length]);
    });
    return map;
  }, [children]);

  // Filter schedules for selected year
  const filteredSchedules = useMemo(() => {
    return children.flatMap(child => 
      (child.camp_schedules || [])
        .filter(schedule => {
          const scheduleYear = getYearFromDateString(schedule.start_date);
          return scheduleYear === selectedYear;
        })
        .map(schedule => ({
          ...schedule,
          child_id: schedule.child_id || child.id,
          child_name: child.name
        }))
    );
  }, [children, selectedYear]);

  // Get schedules for the selected month
  const monthSchedules = useMemo(() => {
    return filteredSchedules.filter(schedule => {
      const startDate = parseDateString(schedule.start_date);
      if (!startDate) return false;
      return startDate.getMonth() === selectedMonth && startDate.getFullYear() === selectedYear;
    });
  }, [filteredSchedules, selectedMonth, selectedYear]);

  // Generate calendar grid for the selected month
  const calendarGrid = useMemo(() => {
    const year = selectedYear;
    const month = selectedMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

    const days: Array<{
      date: number;
      dateString: string;
      schedules: typeof monthSchedules;
    }> = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({
        date: 0,
        dateString: '',
        schedules: []
      });
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const daySchedules = monthSchedules.filter(schedule => {
        const startDate = parseDateString(schedule.start_date);
        const endDate = parseDateString(schedule.end_date);
        if (!startDate || !endDate) return false;
        
        const currentDate = new Date(year, month, day);
        return currentDate >= startDate && currentDate <= endDate;
      });

      days.push({
        date: day,
        dateString,
        schedules: daySchedules
      });
    }

    return days;
  }, [selectedYear, selectedMonth, monthSchedules]);

  // Get child by ID
  const getChild = (childId: string) => {
    return children.find(c => c.id === childId);
  };

  return (
    <div className="calendar-preview" style={{ display: 'block', visibility: 'visible' }} data-calendar-content>
      {/* Header with Month/Year Selectors */}
      <div className="mb-6 print:mb-4">
        <div className="flex items-center justify-between mb-4 print:mb-2">
          <h2 className="text-2xl font-semibold text-gray-900 print:text-xl">
            {MONTHS[selectedMonth]} {selectedYear}
          </h2>
          <div className="flex items-center gap-3 print:hidden" data-print-hide>
            {/* Year Selector */}
            {availableYears.length > 0 && (
              <Listbox value={selectedYear} onChange={handleYearChange}>
                <div className="relative">
                  <Listbox.Button className="inline-flex items-center justify-between gap-1.5 w-32 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary">
                    <span>{selectedYear}</span>
                    <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Listbox.Options className="absolute right-0 mt-2 w-32 max-h-60 overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      {availableYears.map((year) => (
                        <Listbox.Option
                          key={year}
                          value={year}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 ${
                              active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                            } ${selectedYear === year ? 'bg-primary/20 font-medium' : ''}`
                          }
                        >
                          {({ selected }) => (
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {year}
                            </span>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            )}
            {/* Month Selector */}
            <Listbox value={selectedMonth} onChange={setSelectedMonth}>
              <div className="relative">
                <Listbox.Button className="inline-flex items-center justify-between gap-1.5 w-40 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary">
                  <span>{MONTHS[selectedMonth]}</span>
                  <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Listbox.Options className="absolute right-0 mt-2 w-40 max-h-60 overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    {MONTHS.map((month, index) => (
                      <Listbox.Option
                        key={index}
                        value={index}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 px-4 ${
                            active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                          } ${selectedMonth === index ? 'bg-primary/20 font-medium' : ''}`
                        }
                      >
                        {({ selected }) => (
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {month}
                          </span>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-sm print:text-xs">
          {children.map(child => (
            <div key={child.id} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: childColorMap.get(child.id) || CHILD_COLORS[0] }}
              />
              <span className="text-gray-700">{child.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2 print:mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div
              key={day}
              className="text-center text-sm font-semibold text-gray-700 py-2 print:text-xs print:py-1"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarGrid.map((day, index) => {
            if (day.date === 0) {
              return (
                <div
                  key={`empty-${index}`}
                  className="aspect-square bg-gray-50 print:bg-white print:border print:border-gray-200"
                />
              );
            }

            const hasMultipleSchedules = day.schedules.length > 1;
            
            return (
              <div
                key={day.dateString}
                className="aspect-square border border-gray-200 bg-white p-1 print:p-0.5 flex flex-col"
              >
                {/* Date Number */}
                <div className="text-xs font-medium text-gray-700 mb-1 print:text-[10px] print:mb-0.5">
                  {day.date}
                </div>

                {/* Schedule Blocks */}
                <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                  {day.schedules.slice(0, 3).map(schedule => {
                    const child = getChild(schedule.child_id || '');
                    const color = child ? childColorMap.get(child.id) || CHILD_COLORS[0] : CHILD_COLORS[0];
                    const startDate = parseDateString(schedule.start_date);
                    const endDate = parseDateString(schedule.end_date);
                    const isStart = startDate && startDate.getDate() === day.date;
                    const isEnd = endDate && endDate.getDate() === day.date;
                    const campName = schedule.camp?.name || 'Unknown Camp';

                    // Convert hex color to rgba with 20% opacity for print
                    const hexToRgba = (hex: string, alpha: number) => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                    };

                    return (
                      <div
                        key={schedule.id}
                        className="text-[10px] px-1 py-0.5 rounded print:text-xs print:px-0.5 print:py-0.5 truncate text-white font-medium print:font-semibold schedule-block"
                        style={{
                          backgroundColor: color,
                          color: 'white',
                          opacity: 1,
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact',
                          colorAdjust: 'exact',
                          '--schedule-color': color,
                          '--schedule-color-rgba': hexToRgba(color, 0.2)
                        } as React.CSSProperties & { '--schedule-color': string; '--schedule-color-rgba': string }}
                        title={`${campName} - ${child?.name || ''}`}
                      >
                        <span className="truncate block text-white print:text-black print:font-semibold">
                          {campName}
                        </span>
                      </div>
                    );
                  })}
                  {day.schedules.length > 3 && (
                    <div className="text-[10px] text-gray-500 print:text-[8px]">
                      +{day.schedules.length - 3}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Print Styles - Additional calendar-specific styles */}
      <style data-print-styles>{`
        @media print {
          /* Ensure calendar preview container is visible */
          .calendar-preview {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: avoid;
            break-inside: avoid;
            background: white !important;
            position: relative !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Ensure all calendar preview children are visible */
          .calendar-preview > * {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          
          /* Calendar grid - keep as grid for layout */
          .calendar-preview .grid {
            display: grid !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Calendar grid cells */
          .calendar-preview .grid > div {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Aspect square cells */
          .calendar-preview .aspect-square {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            min-height: 80px !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Hide print:hidden elements - be very explicit */
          .print\\:hidden,
          [class*="print:hidden"],
          .print\\:hidden *,
          [class*="print:hidden"] * {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
          
          /* Hide Listbox components in print */
          [role="listbox"],
          [role="option"],
          button[aria-haspopup="listbox"],
          [data-print-hide],
          [data-print-hide] * {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
          
          /* Ensure colored schedule blocks print with 20% opacity background and dark text */
          .calendar-preview .schedule-block {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            display: block !important;
            visibility: visible !important;
            position: relative !important;
            background-color: var(--schedule-color-rgba) !important; /* Use rgba with 20% opacity */
          }
          
          /* Ensure text is dark and bold in print for readability on light background */
          .calendar-preview .schedule-block span,
          .calendar-preview .schedule-block {
            color: #000 !important; /* Black text for better readability on light background */
            font-weight: 600 !important;
            font-size: 0.75rem !important; /* 12px - larger font for print */
          }
          
          /* Ensure all text elements are visible */
          .calendar-preview h2,
          .calendar-preview h3,
          .calendar-preview p,
          .calendar-preview span,
          .calendar-preview div,
          .calendar-preview button {
            color: #000 !important;
            visibility: visible !important;
          }
          
          /* Keep flex layouts for calendar cells */
          .calendar-preview .flex {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          
          /* Ensure legend is visible */
          .calendar-preview .flex-wrap {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          
          /* Hide style tags in print */
          style[data-print-styles] {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>
    </div>
  );
}

